import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Activity, MapPin, Eye, Users, Globe, RefreshCw, Smartphone, Monitor, Tablet, ArrowUpRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PERIODS = [
  { id: 'today', label: 'Oggi' },
  { id: 'week', label: '7 giorni' },
  { id: 'month', label: '30 giorni' },
  { id: 'all', label: 'Sempre' },
];

const REFERRER_COLORS = {
  direct: '#94A3B8',
  facebook: '#1877F2',
  instagram: '#E1306C',
  whatsapp: '#25D366',
  search: '#10B981',
  linkedin: '#0A66C2',
  youtube: '#FF0000',
  twitter: '#0F141A',
  tiktok: '#000000',
  other: '#FF6B35',
};

const DEVICE_ICONS = { mobile: Smartphone, desktop: Monitor, tablet: Tablet };

export const AnalyticsPanel = ({ token }) => {
  const [realtime, setRealtime] = useState({ online: 0, active_cities: 0 });
  const [summary, setSummary] = useState(null);
  const [recent, setRecent] = useState([]);
  const [period, setPeriod] = useState('week');
  const [loading, setLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  const headers = { 'X-Admin-Token': token };

  const fetchRealtime = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/analytics/realtime`, { headers });
      setRealtime(r.data);
    } catch {/* silent */}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [r, s, rec] = await Promise.all([
        axios.get(`${API}/admin/analytics/realtime`, { headers }),
        axios.get(`${API}/admin/analytics/summary?period=${period}`, { headers }),
        axios.get(`${API}/admin/analytics/recent?limit=30`, { headers }),
      ]);
      setRealtime(r.data);
      setSummary(s.data);
      setRecent(rec.data?.events || []);
      setLastRefresh(new Date());
    } catch {/* silent */}
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, period]);

  useEffect(() => {
    fetchAll();
    // Realtime poller every 20s (lightweight endpoint)
    const id = setInterval(fetchRealtime, 20 * 1000);
    return () => clearInterval(id);
  }, [fetchAll, fetchRealtime]);

  const maxBar = Math.max(
    ...((summary?.top_cities_italy || []).map((c) => c.count)),
    ...((summary?.top_cities_world || []).map((c) => c.count)),
    1
  );

  return (
    <div className="space-y-6" data-testid="pv-admin-analytics-panel">
      {/* === ONLINE NOW HERO === */}
      <div className="relative overflow-hidden bg-gradient-to-br from-pv-ink via-pv-tealDeep to-pv-teal text-white rounded-2xl p-6 md:p-7">
        <div className="absolute inset-0 opacity-20 pointer-events-none"
             style={{ background: 'radial-gradient(circle at 80% 20%, rgba(255,107,53,0.4) 0%, transparent 50%)' }} />
        <div className="relative flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-white/70 font-bold mb-2">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400"></span>
              </span>
              ONLINE ORA · ultimo aggiornamento {lastRefresh ? lastRefresh.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—'}
            </div>
            <div className="flex items-baseline gap-4 flex-wrap">
              <div className="text-[68px] md:text-[88px] font-black leading-none tracking-normal" data-testid="pv-admin-online-count">
                {realtime.online}
              </div>
              <div className="text-white/70 text-[15px] md:text-[17px] font-medium">
                {realtime.online === 1 ? 'persona sul sito' : 'persone sul sito'}
                <div className="text-[12px] text-white/50 mt-0.5">
                  in <strong>{realtime.active_cities}</strong> {realtime.active_cities === 1 ? 'città' : 'città'} negli ultimi 5 minuti
                </div>
              </div>
            </div>
          </div>
          <button
            onClick={fetchAll}
            disabled={loading}
            data-testid="pv-admin-analytics-refresh"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/15 border border-white/15 text-white text-[12.5px] font-bold transition-colors backdrop-blur-sm"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Aggiorna
          </button>
        </div>
      </div>

      {/* === PERIOD SWITCH === */}
      <div className="flex flex-wrap items-center gap-2">
        {PERIODS.map((p) => (
          <button
            key={p.id}
            onClick={() => setPeriod(p.id)}
            data-testid={`pv-admin-analytics-period-${p.id}`}
            className={`px-4 py-2 rounded-xl text-[12.5px] font-bold transition-colors ${
              period === p.id ? 'bg-pv-teal text-white shadow-[0_4px_12px_-4px_rgba(27,127,184,0.5)]' : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* === KPI CARDS === */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPICard
          icon={Eye}
          label="Visualizzazioni"
          value={summary?.total_pageviews ?? 0}
          color="bg-pv-mist border-pv-teal/30"
          iconColor="text-pv-teal"
          testid="pv-admin-kpi-pageviews"
        />
        <KPICard
          icon={Users}
          label="Visitatori unici"
          value={summary?.unique_visitors ?? 0}
          color="bg-orange-50 border-pv-orange/30"
          iconColor="text-pv-orange"
          testid="pv-admin-kpi-uniques"
        />
        <KPICard
          icon={MapPin}
          label="Città italiane"
          value={summary?.top_cities_italy?.length ?? 0}
          color="bg-emerald-50 border-emerald-300"
          iconColor="text-emerald-600"
          testid="pv-admin-kpi-cities"
        />
        <KPICard
          icon={Globe}
          label="Sorgenti traffico"
          value={summary?.top_referrers?.length ?? 0}
          color="bg-violet-50 border-violet-300"
          iconColor="text-violet-600"
          testid="pv-admin-kpi-refs"
        />
      </div>

      {/* === TOP CITIES ITALY === */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Città italiane più attive" badge="ITALIA" badgeColor="bg-emerald-100 text-emerald-700" testid="pv-admin-cities-italy">
          {(summary?.top_cities_italy?.length || 0) === 0 ? (
            <Empty label="Nessuna visita dall'Italia ancora — il tracking si popola appena qualcuno aprirà il sito" />
          ) : (
            <BarList items={summary.top_cities_italy} max={maxBar} accent="#10B981" />
          )}
        </Card>

        <Card title="Top città nel mondo" badge="GLOBAL" badgeColor="bg-pv-mist text-pv-tealDeep" testid="pv-admin-cities-world">
          {(summary?.top_cities_world?.length || 0) === 0 ? (
            <Empty label="Ancora nessun dato" />
          ) : (
            <BarList items={summary.top_cities_world} max={maxBar} accent="#1B7FB8" />
          )}
        </Card>
      </div>

      {/* === TOP PAGES + REFERRERS === */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Pagine più viste" badge="PAGINE" badgeColor="bg-pv-mist text-pv-tealDeep" testid="pv-admin-top-pages">
          {(summary?.top_pages?.length || 0) === 0 ? (
            <Empty label="Nessun dato" />
          ) : (
            <BarList items={summary.top_pages} max={Math.max(...summary.top_pages.map((p) => p.count), 1)} accent="#1B7FB8" mono />
          )}
        </Card>

        <Card title="Da dove arrivano i visitatori" badge="SORGENTI" badgeColor="bg-orange-100 text-pv-orangeDeep" testid="pv-admin-top-referrers">
          {(summary?.top_referrers?.length || 0) === 0 ? (
            <Empty label="Nessun dato" />
          ) : (
            <BarList
              items={summary.top_referrers}
              max={Math.max(...summary.top_referrers.map((p) => p.count), 1)}
              colorByLabel={(lbl) => REFERRER_COLORS[lbl] || '#94A3B8'}
            />
          )}
        </Card>
      </div>

      {/* === RECENT VISITS === */}
      <Card title="Ultime visite" badge={`${recent.length} EVENTI`} badgeColor="bg-pv-mist text-pv-tealDeep" testid="pv-admin-recent">
        {recent.length === 0 ? (
          <Empty label="Ancora nessuna visita registrata" />
        ) : (
          <div className="max-h-80 overflow-auto rounded-xl border border-slate-200 bg-slate-50/50">
            <table className="w-full text-[12.5px]">
              <thead className="sticky top-0 bg-white border-b border-slate-200">
                <tr className="text-left text-[10.5px] font-mono uppercase tracking-wider text-slate-500">
                  <th className="px-3 py-2.5">Quando</th>
                  <th className="px-3 py-2.5">Città</th>
                  <th className="px-3 py-2.5">Pagina</th>
                  <th className="px-3 py-2.5">Da</th>
                  <th className="px-3 py-2.5">Device</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((ev) => {
                  const DevIcon = DEVICE_ICONS[ev.device] || Monitor;
                  return (
                    <tr key={ev.event_id} className="border-b border-slate-100 hover:bg-white">
                      <td className="px-3 py-2.5 font-mono text-[10.5px] text-slate-500 whitespace-nowrap">
                        {new Date(ev.ts).toLocaleString('it-IT', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })}
                      </td>
                      <td className="px-3 py-2.5 font-medium text-pv-ink whitespace-nowrap">
                        {ev.city ? (
                          <span className="inline-flex items-center gap-1">
                            <MapPin className="w-3 h-3 text-pv-teal" />
                            {ev.city}
                            {ev.country_code && <span className="text-[10px] text-slate-400 font-mono">·{ev.country_code}</span>}
                          </span>
                        ) : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-3 py-2.5 font-mono text-[11px] text-slate-700 truncate max-w-xs">{ev.path}</td>
                      <td className="px-3 py-2.5">
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-bold uppercase tracking-wide text-white"
                          style={{ background: REFERRER_COLORS[ev.referrer_kind] || '#94A3B8' }}
                        >
                          {ev.referrer_kind}
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        <DevIcon className="w-3.5 h-3.5 text-slate-500" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

// ---------- Sub-components ----------

const KPICard = ({ icon: Icon, label, value, color, iconColor, testid }) => (
  <div className={`border rounded-2xl p-4 ${color}`} data-testid={testid}>
    <div className="flex items-center justify-between mb-2">
      <Icon className={`w-5 h-5 ${iconColor}`} />
      <ArrowUpRight className="w-3.5 h-3.5 text-slate-300" />
    </div>
    <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500 font-bold">{label}</div>
    <div className="text-pv-ink font-black text-[28px] md:text-[32px] leading-none mt-1">{value}</div>
  </div>
);

const Card = ({ title, badge, badgeColor, children, testid }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={testid}>
    <div className="flex items-center justify-between mb-4">
      <h3 className="font-heading font-extrabold text-pv-ink text-[15px]">{title}</h3>
      <span className={`text-[10px] font-mono uppercase tracking-[0.18em] font-bold px-2 py-1 rounded-full ${badgeColor}`}>{badge}</span>
    </div>
    {children}
  </div>
);

const BarList = ({ items, max, accent = '#1B7FB8', mono = false, colorByLabel = null }) => (
  <div className="space-y-2.5">
    {items.map((item, i) => {
      const pct = max ? (item.count / max) * 100 : 0;
      const c = colorByLabel ? colorByLabel(item.label) : accent;
      return (
        <div key={`${item.label}-${i}`} className="group">
          <div className="flex items-center justify-between text-[12.5px] mb-1">
            <span className={`text-pv-ink ${mono ? 'font-mono text-[11.5px]' : 'font-semibold'} truncate max-w-[70%]`}>
              {item.label}
            </span>
            <span className="font-mono font-bold text-pv-ink text-[12px]">{item.count}</span>
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${pct}%`, background: c }}
            />
          </div>
        </div>
      );
    })}
  </div>
);

const Empty = ({ label }) => (
  <div className="text-center py-8 text-[12.5px] text-slate-400">{label}</div>
);

export default AnalyticsPanel;
